import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { searchInstruments } from '@/trading/tradingApi'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { useOrderSource } from '@/common/context/useOrderSource'
import { ORDER_ENTRY_POINT, resolveOrderFromPathname } from '@/common/context/orderSourceValues'
import { InstrumentSearchRecordT } from '@/common/types'

type Args = {
    isDesktop: boolean
    onItemClick?: () => void
}

export function useInstrumentSearch({ isDesktop, onItemClick }: Args) {
    const location = useLocation()
    const { setOrderSource } = useOrderSource()

    const [searchTerm, setSearchTerm] = useState('')
    const [searchResult, setSearchResult] = useState<InstrumentSearchRecordT[]>([])
    const [searching, setSearching] = useState(false)
    const [showPopover, setShowPopover] = useState(false)
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const latestRequestIdRef = useRef(0)

    const cancelPendingSearch = useCallback(() => {
        latestRequestIdRef.current += 1
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
        }
    }, [])

    const fetchInstruments = useCallback(
        (query: string) => {
            const requestId = ++latestRequestIdRef.current
            analytics.trackWithDefaults(ANALYTICS_EVENTS.INSTRUMENT_SEARCH.SEARCH_PERFORMED, {
                searchedFor: query,
            })
            setSearching(true)
            if (isDesktop) {
                setShowPopover(true)
            }
            searchInstruments(query)
                .then((res) => {
                    if (requestId !== latestRequestIdRef.current) return
                    setSearchResult(res.matches)
                })
                .finally(() => {
                    if (requestId !== latestRequestIdRef.current) return
                    setSearching(false)
                })
        },
        [isDesktop],
    )

    const clearSearch = useCallback(() => {
        cancelPendingSearch()
        setSearchTerm('')
        setSearchResult([])
        setSearching(false)
        if (isDesktop) {
            setShowPopover(false)
        }
    }, [cancelPendingSearch, isDesktop])

    useEffect(() => {
        return () => {
            cancelPendingSearch()
        }
    }, [cancelPendingSearch])

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value
        setSearchTerm(query)

        if (query.length === 0) {
            clearSearch()
            return
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
        }

        if (query.length < 2) {
            latestRequestIdRef.current += 1
            setSearching(false)
            setSearchResult([])
            if (isDesktop) {
                setShowPopover(false)
            }
            return
        }

        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null
            fetchInstruments(query)
        }, 500)
    }

    const onResultClick = (security: InstrumentSearchRecordT) => {
        setOrderSource(resolveOrderFromPathname(location.pathname), ORDER_ENTRY_POINT.INSTRUMENT_SEARCH)
        onItemClick?.()
        clearSearch()
        analytics.trackWithDefaults(ANALYTICS_EVENTS.INSTRUMENT_SEARCH.RESULT_CLICKED, {
            security_code: security.securityCode,
        })
    }

    return {
        searchTerm,
        searching,
        searchResult,
        showPopover,
        setShowPopover,
        clearSearch,
        onSearchChange,
        onResultClick,
    }
}
